import { Dispatch } from 'redux';

import { getPlansForDate } from 'modules/effectiveDate/services/EffectiveDateService';
import PlansForDate from 'modules/effectiveDate/types';
import PlanForDate from 'model/PlanForDate';

// What the list thunks need from the store, spelled out so the slices do not
// import the store's own types (the store imports the slices).
export type GuideGetState = () => {
  benguide: { data: { storedEffectiveDate?: string } | null };
};

// The date the guide's plan lists are built for, or null when a guide was
// legitimately saved without a date.
// `storedEffectiveDate` and not `effectiveDate`: the latter is also filled for
// an undated guide, from its plan year.
export const getDatedEffectiveDate = async (
  getState: GuideGetState
): Promise<string | null> =>
  getState().benguide.data?.storedEffectiveDate || null;

const withPlanForDate = (item: any, planForDate: PlanForDate) => ({
  ...item,
  planForDate,
});

// Joins the per-plan-year reads in the shape the old read had: a page keeps
// its other fields, an array stays an array.
const merge = (datas: any[], keep: (item: any) => any | null) => {
  const first = datas[0];
  if (Array.isArray(first)) {
    return datas.flatMap((data) => data.map(keep).filter(Boolean));
  }
  if (Array.isArray(first?.content)) {
    const content = datas.flatMap((data) =>
      data.content.map(keep).filter(Boolean)
    );
    return {
      ...first,
      content,
      ...('totalElements' in first ? { totalElements: content.length } : {}),
      ...('numberOfElements' in first
        ? { numberOfElements: content.length }
        : {}),
      ...('totalPages' in first ? { totalPages: 1 } : {}),
    };
  }
  return first;
};

export type DatedReadOptions = {
  // Set when the old read was asked for hidden plans (`allow-hidden`). The
  // for-date read never lists a hidden plan, so a plan this read returns from
  // one of the plan years covering the date for this line is kept as it came.
  hiddenPlansOf?: string;
};

// One plans-for-date read says which plans are active on the date and which
// plan years cover it; each covering plan year is then read with the same
// call the old path makes, and only the active plans are kept.
export const listPlansForGuide = async <R extends { data: any }>(
  employerId: string,
  effectiveDate: string,
  legacyPlanYearId: string,
  read: (planYearId: string) => Promise<R>,
  options: DatedReadOptions = {}
): Promise<R> => {
  const { data } = await getPlansForDate(employerId, effectiveDate);
  const allowed = indexActivePlans(data);
  const hiddenKeptFrom = new Set(
    (
      data.lines.find((line) => line.benefitKind === options.hiddenPlansOf)
        ?.planYears ?? []
    ).map((planYear) => planYear.id)
  );

  const planYearIds = Array.from(
    new Set(Object.values(allowed).map(({ planYearId }) => planYearId))
  );
  // With no covering plan year there is nothing to list, but the empty list
  // must still have the shape the old read has, so one read is made and
  // everything in it is dropped.
  const responses = await Promise.all(
    (planYearIds.length > 0 ? planYearIds : [legacyPlanYearId]).map((id) =>
      read(id)
    )
  );

  const keep = (item: any) => {
    const active = allowed[item.id];
    if (active) {
      return withPlanForDate(item, {
        effectiveDate: active.effectiveDate,
        planYearName: active.planYearName,
        isDefault: active.default,
      });
    }
    return hiddenKeptFrom.has(item.planYearId) ? item : null;
  };

  return {
    ...responses[0],
    data: merge(
      responses.map((response) => response.data),
      keep
    ),
  };
};

const indexActivePlans = (data: PlansForDate) => {
  const index: Record<
    string,
    {
      planYearId: string;
      planYearName: string;
      effectiveDate: string;
      default: boolean;
    }
  > = {};
  data.lines.forEach((line) =>
    line.plans.forEach((plan) => {
      if (plan.masterId !== null) {
        index[plan.masterId] = plan;
      }
    })
  );
  return index;
};

// The one call every list thunk makes: the old read, unchanged, unless the
// guide has a date.
export const readGuidePlans = async <R extends { data: any }>(
  dispatch: Dispatch,
  getState: GuideGetState,
  employerId: string,
  legacyPlanYearId: string,
  read: (planYearId: string) => Promise<R>,
  options?: DatedReadOptions
): Promise<R> => {
  const effectiveDate = await getDatedEffectiveDate(getState);
  if (!effectiveDate) {
    return read(legacyPlanYearId);
  }
  return listPlansForGuide(
    employerId,
    effectiveDate,
    legacyPlanYearId,
    read,
    options
  );
};
